import { type FC, type ReactNode } from 'react'
import { Form } from 'react-bootstrap'
import styles from './CalendarInvitePreview.module.scss'
import { PROVIDER_CONFIG, type ProviderConfig } from '../../../common/meetingProviders'
import { IntegrationIcon } from '../../../components/IntegrationIcon'
import { type BookingInvitee, type Location, MeetingMode, MeetingPlatformProvider, type SchedulingEventType } from '../../../models/Scheduling'
import { type LocationInput } from '../../../store/scheduling/dto'
import { type BookingSlot } from './form/timeSlots'
import { clientDisplayName } from '../shared/schedulingDisplay'
import { formatInviteWhen } from '../bookingFormat'

interface CalendarInvitePreviewProps {
  eventType: SchedulingEventType
  ownerName: string
  invitees: BookingInvitee[]
  meetingMode: MeetingMode
  meetingPlatform?: MeetingPlatformProvider | null
  joinLinkBroken?: boolean
  slot: BookingSlot | null
  title: string
  timeZone?: string
  timezoneLabel: string
  location: Location | LocationInput | null
  note: string
  onNoteChange: (note: string) => void
}

// Renders into the parent's two-column grid (label cell + value cell)
const PreviewRow: FC<{ label: string; children: ReactNode }> = ({ label, children }) => (
  <>
    <div className='paragraph-small zocks-text-gray-500'>{label}</div>
    <div className='paragraph-small'>{children}</div>
  </>
)

const PLATFORM_CONFIG: Record<MeetingPlatformProvider, ProviderConfig | undefined> = {
  [MeetingPlatformProvider.ZOOM]: PROVIDER_CONFIG.zoom,
  [MeetingPlatformProvider.GOOGLE_MEET]: PROVIDER_CONFIG.meet,
  [MeetingPlatformProvider.MS_TEAMS]: PROVIDER_CONFIG.teams,
}

const PlatformPill: FC<{ platform?: MeetingPlatformProvider | null }> = ({ platform }) => {
  const config = platform ? PLATFORM_CONFIG[platform] : undefined
  return (
    <span className='d-inline-flex align-items-center zocks-gap-0.25 badge zocks-bg-color-gray-100 zocks-text-gray-900'>
      {config ? <IntegrationIcon serviceId={config.serviceId} width={16} height={16} /> : <i className='bi bi-camera-video' aria-hidden='true' />}
      {config?.label ?? 'Video call'}
    </span>
  )
}

const JoinLinkWarning: FC = () => (
  <span className='d-inline-flex align-items-center zocks-gap-0.5'>
    <i className='bi bi-exclamation-triangle-fill zocks-text-warning-dark' aria-hidden='true' />
    <span className='zocks-text-gray-600'>Join link will not be added</span>
  </span>
)

interface LocationPreviewProps {
  meetingMode: MeetingMode
  meetingPlatform?: MeetingPlatformProvider | null
  location: Location | LocationInput | null
  joinLinkBroken: boolean
}

const LocationPreview: FC<LocationPreviewProps> = ({ meetingMode, meetingPlatform, location, joinLinkBroken }) => {
  if (meetingMode !== MeetingMode.IN_PERSON) {
    return joinLinkBroken ? <JoinLinkWarning /> : <PlatformPill platform={meetingPlatform} />
  }
  if (!location?.address) {
    return <span className='zocks-text-gray-500'>Set location</span>
  }
  return (
    <>
      {location.address}
      {location.note ? ` · ${location.note}` : ''}
    </>
  )
}

// Right column for the "Propose a specific time" mode: the calendar invite the client receives.
export const CalendarInvitePreview: FC<CalendarInvitePreviewProps> = ({
  eventType,
  ownerName,
  invitees,
  meetingMode,
  meetingPlatform,
  joinLinkBroken = false,
  slot,
  title,
  timeZone,
  timezoneLabel,
  location,
  note,
  onNoteChange,
}) => {
  const inviteeNames = invitees.map((invitee) => clientDisplayName({ firstName: invitee.firstName, lastName: invitee.lastName, email: invitee.email }))
  const withWho = [ownerName, ...inviteeNames].filter(Boolean).join(', ')
  const whenText = slot ? `${formatInviteWhen(slot.startAt, timeZone)} · ${timezoneLabel || 'Set timezone'}` : 'Pick a time'

  return (
    <div className='d-flex flex-column zocks-gap-0.5'>
      <h5 className='mb-0'>Calendar invite</h5>
      <div className='paragraph-small zocks-text-gray-600'>This event gets sent to the client and gets added to the selected user&apos;s calendar.</div>
      <div className='d-flex flex-column gap-3 p-3 mt-2 border zocks-border-color-gray-200 zocks-border-1.0 zocks-bg-color-white'>
        {title ? <div className='bold'>{title}</div> : null}
        <div className={styles.rows}>
          <PreviewRow label='Meeting'>
            {eventType.name} · {eventType.durationMinutes} min
          </PreviewRow>
          <PreviewRow label='When'>{whenText}</PreviewRow>
          <PreviewRow label='Where'>
            <LocationPreview meetingMode={meetingMode} meetingPlatform={meetingPlatform} location={location} joinLinkBroken={joinLinkBroken} />
          </PreviewRow>
          <PreviewRow label='With'>{withWho}</PreviewRow>
        </div>
        <hr className='my-0 zocks-text-gray-300' />
        <div className='d-flex flex-column zocks-gap-0.25'>
          <label className='bold' htmlFor='schedule-meeting-note'>
            Note to client
          </label>
          <Form.Control
            as='textarea'
            id='schedule-meeting-note'
            rows={4}
            value={note}
            placeholder='You can reschedule or cancel this meeting here: [Link]'
            onChange={(event) => onNoteChange(event.currentTarget.value)}
          />
        </div>
      </div>
    </div>
  )
}
